// src/hooks/useDeviceDetection.js - Hook personalizado para detección de dispositivos
import { useState, useEffect } from 'react';

export const useDeviceDetection = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: false,
    isHuaweiDevice: false,
    deviceOrientation: 'portrait',
    nativeCameraSupported: false,
    mediaRecorderSupported: false,
    webRTCSupported: false
  });

  useEffect(() => {
    const detectDevice = () => {
      // Detectar si es móvil/tablet
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isTablet = /ipad|android(?=.*\b(?!.*mobile))/i.test(userAgent);
      const isDesktop = !isMobile && !isTablet;

      // Detectar si es dispositivo Huawei
      const isHuaweiDevice = /huawei|honor/i.test(userAgent);

      // Detectar orientación del dispositivo
      const getDeviceOrientation = () => {
        if (window.innerWidth > window.innerHeight) {
          return 'landscape';
        }
        return 'portrait';
      };

      // Verificar soporte para tecnologías
      const checkTechnologySupport = () => {
        // Soporte para MediaRecorder
        const mediaRecorderSupported = typeof MediaRecorder !== 'undefined';
        
        // Soporte para WebRTC
        const webRTCSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
        
        // Soporte para cámara nativa (input file con capture)
        const nativeCameraSupported = 'capture' in document.createElement('input');

        return {
          mediaRecorderSupported,
          webRTCSupported,
          nativeCameraSupported
        };
      };

      // Verificar capacidades de cámara
      const checkCameraCapabilities = async () => {
        try {
          if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const videoDevices = devices.filter(device => device.kind === 'videoinput');
            const audioDevices = devices.filter(device => device.kind === 'audioinput');
            
            return {
              hasCamera: videoDevices.length > 0,
              hasMicrophone: audioDevices.length > 0,
              cameraCount: videoDevices.length,
              audioCount: audioDevices.length
            };
          }
          return {
            hasCamera: false,
            hasMicrophone: false,
            cameraCount: 0,
            audioCount: 0
          };
        } catch (error) {
          console.warn('No se pudieron verificar las capacidades de cámara:', error);
          return {
            hasCamera: false,
            hasMicrophone: false,
            cameraCount: 0,
            audioCount: 0
          };
        }
      };

      // Función para actualizar orientación
      const updateOrientation = () => {
        setDeviceInfo(prev => ({
          ...prev,
          deviceOrientation: getDeviceOrientation()
        }));
      };

      // Verificar soporte de tecnologías
      const techSupport = checkTechnologySupport();

      // Configurar información inicial del dispositivo
      setDeviceInfo({
        isMobile,
        isTablet,
        isDesktop,
        isHuaweiDevice,
        deviceOrientation: getDeviceOrientation(),
        ...techSupport
      });

      // Agregar listeners para cambios de orientación
      window.addEventListener('resize', updateOrientation);
      window.addEventListener('orientationchange', updateOrientation);

      // Verificar capacidades de cámara
      checkCameraCapabilities().then(cameraCapabilities => {
        setDeviceInfo(prev => ({
          ...prev,
          ...cameraCapabilities
        }));
      });

      // Cleanup
      return () => {
        window.removeEventListener('resize', updateOrientation);
        window.removeEventListener('orientationchange', updateOrientation);
      };
    };

    detectDevice();
  }, []);

  // Función para obtener información del dispositivo en tiempo real
  const getDeviceInfo = () => {
    return deviceInfo;
  };

  // Función para verificar si el dispositivo soporta una funcionalidad específica
  const supportsFeature = (feature) => {
    switch (feature) {
      case 'camera':
        return deviceInfo.hasCamera;
      case 'microphone':
        return deviceInfo.hasMicrophone;
      case 'mediaRecorder':
        return deviceInfo.mediaRecorderSupported;
      case 'webRTC':
        return deviceInfo.webRTCSupported;
      case 'nativeCamera':
        return deviceInfo.nativeCameraSupported;
      default:
        return false;
    }
  };

  // Función para obtener recomendaciones según el dispositivo
  const getDeviceRecommendations = () => {
    const recommendations = [];

    if (deviceInfo.isHuaweiDevice) {
      recommendations.push('Dispositivo Huawei detectado - Se recomienda usar modo nativo para mejor compatibilidad');
    }

    if (!deviceInfo.mediaRecorderSupported) {
      recommendations.push('MediaRecorder no soportado - Se usará modo nativo para grabación');
    }

    if (!deviceInfo.webRTCSupported) {
      recommendations.push('WebRTC no soportado - Funcionalidad de cámara limitada');
    }

    if (deviceInfo.isMobile && deviceInfo.deviceOrientation === 'portrait') {
      recommendations.push('Dispositivo en modo vertical - Optimizado para captura');
    }

    return recommendations;
  };

  return {
    // Estados
    ...deviceInfo,
    
    // Funciones
    getDeviceInfo,
    supportsFeature,
    getDeviceRecommendations
  };
};
